import {
  FlowNode,
  LayoutItem,
  ConnectorLine,
  LayoutResult,
  CARD_WIDTH,
  CARD_HEIGHT,
  H_GAP,
  V_GAP,
} from '../types/flow';

let connectorCounter = 0;

function nextConnectorId(): string {
  connectorCounter++;
  return `conn_${connectorCounter}`;
}

/**
 * Calculates the total height of a branch (array of FlowNodes).
 * For condition nodes, sums the heights of all sub-branches.
 */
function branchHeight(nodes: FlowNode[]): number {
  let maxH = CARD_HEIGHT;

  for (const node of nodes) {
    if (node.type === 'condition' && node.branches && node.branches.length > 0) {
      let totalBranchH = 0;
      for (let i = 0; i < node.branches.length; i++) {
        totalBranchH += branchHeight(node.branches[i]);
        if (i < node.branches.length - 1) totalBranchH += V_GAP;
      }
      maxH = Math.max(maxH, totalBranchH);
    }
  }

  return maxH;
}

/**
 * Recursively computes layout positions for a linear sequence of nodes.
 * Returns the items, connectors, and the X position after the last node.
 */
function layoutSequence(
  nodes: FlowNode[],
  startX: number,
  centerY: number
): { items: LayoutItem[]; connectors: ConnectorLine[]; endX: number } {
  const items: LayoutItem[] = [];
  const connectors: ConnectorLine[] = [];
  let curX = startX;

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];

    if (node.type === 'condition' && node.branches && node.branches.length > 0) {
      // Place the condition node itself
      const condItem: LayoutItem = {
        node,
        x: curX,
        y: centerY - CARD_HEIGHT / 2,
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
      };
      items.push(condItem);

      const condRightX = curX + CARD_WIDTH;
      const condCenterY = centerY;

      // Calculate total height of all branches
      const branchHeights = node.branches.map((b) =>
        b.length > 0 ? branchHeight(b) : CARD_HEIGHT
      );
      const totalH =
        branchHeights.reduce((sum, h) => sum + h, 0) +
        (branchHeights.length - 1) * V_GAP;

      // Position each branch centered around the condition node
      let branchTopY = condCenterY - totalH / 2;
      let maxBranchEndX = condRightX + H_GAP;

      for (let bi = 0; bi < node.branches.length; bi++) {
        const branch = node.branches[bi];
        const bh = branchHeights[bi];
        const branchCenterY = branchTopY + bh / 2;

        // Connector from condition to branch start
        const branchLabel = bi === 0 ? (node.config.true_label as string || 'Si') : (node.config.false_label as string || 'No');
        connectors.push({
          id: nextConnectorId(),
          from: { x: condRightX, y: condCenterY },
          to: { x: condRightX + H_GAP, y: branchCenterY },
          label: branchLabel,
        });

        if (branch.length > 0) {
          const branchResult = layoutSequence(
            branch,
            condRightX + H_GAP,
            branchCenterY
          );
          items.push(...branchResult.items);
          connectors.push(...branchResult.connectors);
          maxBranchEndX = Math.max(maxBranchEndX, branchResult.endX);
        }

        branchTopY += bh + V_GAP;
      }

      curX = maxBranchEndX + H_GAP;
    } else {
      // Regular node
      const item: LayoutItem = {
        node,
        x: curX,
        y: centerY - CARD_HEIGHT / 2,
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
      };
      items.push(item);

      // Connector to next node (if exists and next isn't handled by condition)
      if (i < nodes.length - 1) {
        connectors.push({
          id: nextConnectorId(),
          from: { x: curX + CARD_WIDTH, y: centerY },
          to: { x: curX + CARD_WIDTH + H_GAP, y: centerY },
        });
      }

      curX += CARD_WIDTH + H_GAP;
    }
  }

  return { items, connectors, endX: curX };
}

/**
 * Main layout function. Takes the top-level flow nodes and computes
 * positions + connectors for the entire graph.
 */
export function layoutFlow(nodes: FlowNode[]): LayoutResult {
  connectorCounter = 0;

  if (nodes.length === 0) {
    return { items: [], connectors: [], totalWidth: 0, totalHeight: 0 };
  }

  const totalH = branchHeight(nodes);
  const centerY = Math.max(totalH / 2 + 40, 200); // 40px top padding, min 200px center
  const startX = 60;

  const { items, connectors, endX } = layoutSequence(nodes, startX, centerY);

  const allYs = items.map((it) => it.y);
  const minY = Math.min(...allYs);
  const maxY = Math.max(...allYs) + CARD_HEIGHT;

  return {
    items,
    connectors,
    totalWidth: endX + 60,
    totalHeight: Math.max(maxY - minY + 120, 400),
  };
}
